import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartPieIcon,
  Bars3Icon,
  ChartBarSquareIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ListBulletIcon,
  PencilIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import { insightsPreviewInflow, insightsPreviewOutflow, insightsRun } from '../api';

const CARD_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f43f5e', '#14b8a6', '#f97316', '#6366f1'];

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Determine what breakdown group to use based on saved rules
const getGroupBy = (reportType, rules) => {
  const fields = rules.map(r => (r.field || '').split('.')[1]);
  
  if (reportType === 'inflow') {
    // If we are filtering by Source, breakdown by Bank
    if (fields.includes('source')) return 'bank';
    // If we are filtering by Bank, breakdown by Source
    if (fields.includes('bank')) return 'source';
    // Default for inflows
    return 'source';
  } else {
    // If we are filtering by Category, breakdown by Sub-category
    if (fields.includes('category')) return 'sub_category';
    // If we are filtering by Sub-category, breakdown by Vendor
    if (fields.includes('sub_category')) return 'vendor';
    // If we are filtering by Vendor, breakdown by Category
    if (fields.includes('vendor')) return 'category';
    // Default for outflows
    return 'category';
  }
};

// Determine layout: always show breakdown if possible
const getLayout = (reportType, rules) => {
  return 'kpi_breakdown';
};

const PAGE_SIZE = 10;

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-100 rounded-xl p-3 shadow-xl text-xs">
      <p className="font-bold text-gray-900 mb-1">{payload[0]?.name}</p>
      <p className="text-blue-600 font-semibold">{fmt(payload[0]?.value)}</p>
    </div>
  );
};

const AdaptiveReportCard = ({ report, timePeriod, customStart, customEnd, onDelete, onEdit, onDuplicate, onResize, isPinned, onTogglePin, onDragStart, onDragOver, onDragEnd }) => {
  const [viewMode, setViewMode] = useState('graph'); // 'graph' | 'list'
  const [chartType, setChartType] = useState('pie');  // 'pie' | 'bar'
  const [totals, setTotals] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [listPage, setListPage] = useState(0);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [dragEnabled, setDragEnabled] = useState(false);
  const [width, setWidth] = useState(380);
  const [height, setHeight] = useState(380);

  const cardRef = React.useRef(null);
  const reportType = report.type; // 'inflow' | 'outflow'

  // Parse rules from the backend report object (handle both snake_case and camelCase)
  const rules = React.useMemo(() => {
    try {
      const raw = reportType === 'inflow'
        ? (report.inflow_rules || report.inflowRules || '[]')
        : (report.outflow_rules || report.outflowRules || '[]');
      return typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
    } catch (e) { return []; }
  }, [report, reportType]);

  const layout = getLayout(reportType, rules);
  const groupBy = getGroupBy(reportType, rules);

  // Sync width and height from report tags
  useEffect(() => {
    if (report && report.tags) {
      const widthTag = report.tags.find(t => t.startsWith('width:'));
      if (widthTag) {
        const valStr = widthTag.replace('width:', '');
        if (valStr.startsWith('col-span-')) {
          const colSpanVal = parseInt(valStr.replace('col-span-', ''));
          if (colSpanVal === 2) setWidth(780);
          else if (colSpanVal === 3) setWidth(1180);
          else setWidth(380);
        } else {
          const val = parseInt(valStr);
          if (!isNaN(val)) setWidth(val);
        }
      } else {
        setWidth(380);
      }

      const heightTag = report.tags.find(t => t.startsWith('height:'));
      if (heightTag) {
        const val = parseInt(heightTag.replace('height:', ''));
        if (!isNaN(val)) setHeight(val);
      } else {
        setHeight(380);
      }
    }
  }, [report]);

  const getSavedPeriod = useCallback(() => {
    const tags = report.tags || [];
    const periodTag = tags.find(t => t.startsWith('period:'));
    if (!periodTag) return null;
    const type = periodTag.replace('period:', '');
    const startTag = tags.find(t => t.startsWith('start:'));
    const endTag = tags.find(t => t.startsWith('end:'));
    return {
      type,
      start: startTag ? startTag.replace('start:', '') : null,
      end: endTag ? endTag.replace('end:', '') : null
    };
  }, [report.tags]);

  const buildPayload = useCallback(() => {
    const savedPeriod = getSavedPeriod();
    const effectivePeriod = savedPeriod ? savedPeriod.type : timePeriod;
    const effectiveStart = savedPeriod ? savedPeriod.start : customStart;
    const effectiveEnd = savedPeriod ? savedPeriod.end : customEnd;

    const apiPeriod = (effectivePeriod === 'custom_range' || effectivePeriod === 'custom_fy') ? 'custom' : effectivePeriod;
    return {
      period: apiPeriod !== 'all' ? { type: apiPeriod, start: effectiveStart, end: effectiveEnd } : null,
      root: { logic: 'AND', rules },
    };
  }, [timePeriod, customStart, customEnd, rules, getSavedPeriod]);

  // Fetch totals + breakdown
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const basePayload = buildPayload();

      // 1. Fetch totals via preview endpoint
      const previewApi = reportType === 'inflow' ? insightsPreviewInflow : insightsPreviewOutflow;
      const previewRes = await previewApi({ ...basePayload, pagination: { limit: 0, offset: 0 } });
      setTotals(previewRes.data);

      // 2. Fetch breakdown if needed
      if (layout === 'kpi_breakdown' && groupBy) {
        const runRes = await insightsRun({
          ...basePayload,
          viz: { type: 'bar', groupBy, metric: 'sum', currency: 'rs', topN: 8 },
        });
        const series = runRes.data?.series || [];
        const mapped = series.map(s => ({
          name: s.key || s.month || 'Other',
          value: reportType === 'inflow' ? (s.inflow || 0) : (s.outflow || 0),
        })).filter(s => s.value > 0)
          .sort((a, b) => b.value - a.value);
        setBreakdown(mapped);
      }
    } catch (e) {
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [buildPayload, layout, groupBy, reportType]);

  // Fetch transaction list (lazy load page)
  const fetchTransactions = useCallback(async (page = 0, append = false) => {
    setListLoading(true);
    try {
      const previewApi = reportType === 'inflow' ? insightsPreviewInflow : insightsPreviewOutflow;
      const res = await previewApi({
        ...buildPayload(),
        pagination: { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
        sort: { field: 'date', dir: 'desc' },
      });
      const items = res.data?.items || [];
      setListTotal(res.data?.total || 0);
      setTransactions(prev => append ? [...prev, ...items] : items);
    } catch (e) { /* silent */ }
    finally { setListLoading(false); }
  }, [buildPayload, reportType]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (viewMode === 'list' && transactions.length === 0) fetchTransactions(0);
  }, [viewMode]);

  const handleLoadMore = () => {
    const nextPage = listPage + 1;
    setListPage(nextPage);
    fetchTransactions(nextPage, true);
  };

  // Resize Drag Listener
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = width;
    const startHeight = height;

    let maxParentWidth = 1200;
    if (cardRef.current && cardRef.current.parentElement) {
      const pWidth = cardRef.current.parentElement.getBoundingClientRect().width;
      if (pWidth > 300) {
        maxParentWidth = pWidth;
      }
    }

    let finalWidth = width;
    let finalHeight = height;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      // Free-form continuous resizing in pixels
      const newWidth = Math.max(300, Math.min(maxParentWidth, startWidth + deltaX));
      const newHeight = Math.max(350, Math.min(800, startHeight + deltaY));
      
      setWidth(newWidth);
      setHeight(newHeight);
      
      finalWidth = newWidth;
      finalHeight = newHeight;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (onResize) {
        onResize(report.id, finalWidth, finalHeight);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // ── RENDER HELPERS ──────────────────────────────────────────────
  const renderMetrics = () => {
    const rs = totals?.sum_amount_rs ?? totals?.inflow?.sum_amount_rs ?? 0;
    const usd = totals?.sum_amount_usd ?? totals?.inflow?.sum_amount_usd ?? null;
    const count = totals?.total ?? 0;
    const showUsd = reportType === 'inflow' && usd !== null;

    const rsStr = fmt(rs);
    const usdStr = fmtUsd(usd);

    const getValFontSize = (str) => {
      if (str.length > 10) return 'text-[11px] sm:text-xs md:text-sm xl:text-base';
      if (str.length > 8) return 'text-xs sm:text-sm md:text-base xl:text-lg';
      return 'text-sm sm:text-base md:text-lg';
    };

    return (
      <div 
        className={`grid gap-2 mb-4 shrink-0 ${showUsd && layout !== 'kpi_only' ? '' : 'grid-cols-2'}`}
        style={showUsd && layout !== 'kpi_only' ? { gridTemplateColumns: '1.3fr 1.3fr 0.8fr' } : undefined}
      >
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl px-2.5 py-2.5 border border-blue-100 min-w-0">
          <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-0.5 truncate">Total RS</p>
          <p className={`font-black text-blue-900 leading-tight truncate ${getValFontSize(rsStr)}`} title={rsStr}>{rsStr}</p>
        </div>
        {showUsd && (
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl px-2.5 py-2.5 border border-emerald-100 min-w-0">
            <p className="text-[9px] font-black text-emerald-500 uppercase tracking-widest mb-0.5 truncate">Total USD</p>
            <p className={`font-black text-emerald-900 leading-tight truncate ${getValFontSize(usdStr)}`} title={usdStr}>{usdStr}</p>
          </div>
        )}
        {layout !== 'kpi_only' && (
          <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl px-2.5 py-2.5 border border-slate-100 min-w-0">
            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5 truncate">Transactions</p>
            <p className="text-sm sm:text-base md:text-lg font-black text-slate-900 leading-tight truncate">{count.toLocaleString('en-IN')}</p>
          </div>
        )}
      </div>
    );
  };

  const renderChart = () => {
    if (layout === 'kpi_only' || breakdown.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-300 py-4">
          <ChartPieIcon className="w-14 h-14 mb-2 opacity-30" />
          <p className="text-xs text-gray-400">No breakdown for this report</p>
        </div>
      );
    }

    if (chartType === 'pie') {
      return (
        <div className="flex-1 flex flex-col min-h-0 justify-center">
          <div className="flex-1 min-h-[130px] w-full relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 0, bottom: 0, left: 0, right: 0 }}>
                <Pie
                  data={breakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius="50%"
                  outerRadius="75%"
                  paddingAngle={3}
                  dataKey="value"
                >
                  {breakdown.map((_, i) => (
                    <Cell key={i} fill={CARD_COLORS[i % CARD_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Custom HTML Legend */}
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 px-1 max-h-[70px] overflow-y-auto shrink-0 border-t border-gray-50 pt-1.5">
            {breakdown.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-700 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CARD_COLORS[idx % CARD_COLORS.length] }} />
                <span className="truncate max-w-[120px]" title={item.name}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 min-h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={breakdown} layout="vertical" margin={{ left: 5, right: 10, top: 0, bottom: 0 }}>
            <XAxis type="number" hide axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={75}
              tick={{ fontSize: 9, fontWeight: 600, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59,130,246,0.05)' }} />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={18}>
              {breakdown.map((_, i) => (
                <Cell key={i} fill={CARD_COLORS[i % CARD_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderList = () => (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-0.5">
      {transactions.length === 0 && !listLoading && (
        <div className="text-center text-gray-400 text-xs py-8 italic">No transactions found</div>
      )}
      {transactions.map((item, i) => {
        const label = reportType === 'inflow'
          ? (item.source || '—')
          : (item.expense || item.sub_category || '—');
        const sub = reportType === 'inflow'
          ? item.bank
          : item.category;
        return (
          <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 hover:bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all group">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-gray-900 truncate">{label}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                {sub ? ` · ${sub}` : ''}
              </p>
            </div>
            <p className={`text-xs font-black whitespace-nowrap ${reportType === 'inflow' ? 'text-emerald-600' : 'text-red-500'}`}>
              {fmt(item.amount_rs)}
            </p>
          </div>
        );
      })}
      {transactions.length < listTotal && (
        <button
          onClick={handleLoadMore}
          disabled={listLoading}
          className="w-full py-2 text-[10px] font-black tracking-widest text-blue-600 hover:bg-blue-50 rounded-xl transition-colors border border-dashed border-blue-200 uppercase mt-1 shrink-0"
        >
          {listLoading ? 'Loading...' : `Load More (${listTotal - transactions.length} remaining)`}
        </button>
      )}
      {listLoading && transactions.length === 0 && (
        <div className="flex justify-center py-6">
          <ArrowPathIcon className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
      )}
    </div>
  );

  return (
    <div 
      ref={cardRef}
      draggable={dragEnabled}
      onDragStart={(e) => {
        if (onDragStart) onDragStart(e);
      }}
      onDragOver={(e) => {
        if (onDragOver) onDragOver(e);
      }}
      onDragEnd={() => {
        setDragEnabled(false);
        if (onDragEnd) onDragEnd();
      }}
      className={`relative bg-white rounded-2xl shadow-sm border flex flex-col overflow-hidden transition-shadow duration-300 hover:shadow-xl ${reportType === 'inflow' ? 'border-emerald-100/80' : 'border-blue-100/80'}`} 
      style={{ width: `${width}px`, height: `${height}px`, maxWidth: '100%', minWidth: '280px' }}
    >
      {/* Top accent bar */}
      <div className={`h-1 w-full shrink-0 ${reportType === 'inflow' ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`} />

      <div className="flex flex-col flex-1 p-5 min-h-0">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 shrink-0">
          <div className="min-w-0 flex-1 mr-2 flex items-start gap-2">
            {/* Grip handle for dragging */}
            {onDragStart && (
              <div
                onMouseDown={() => setDragEnabled(true)}
                onMouseUp={() => setDragEnabled(false)}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 mt-1 shrink-0 p-0.5"
                title="Hold to drag and reorder"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 6a1 1 0 110-2 1 1 0 010 2zM7 11a1 1 0 110-2 1 1 0 010 2zM7 16a1 1 0 110-2 1 1 0 010 2zM13 6a1 1 0 110-2 1 1 0 010 2zM13 11a1 1 0 110-2 1 1 0 010 2zM13 16a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-black text-gray-900 leading-tight break-words" title={report.name}>{report.name}</h3>
              <span className={`inline-block mt-1 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${reportType === 'inflow' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}>
                {reportType}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {/* Pin to Dashboard button */}
            {onTogglePin && (
              <button
                onClick={() => onTogglePin(report.id)}
                title={isPinned ? "Unpin from Dashboard" : "Pin to Dashboard"}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <svg 
                  className={`w-3.5 h-3.5 ${isPinned ? 'text-blue-600 fill-blue-600' : 'text-gray-400 hover:text-blue-500'}`} 
                  fill={isPinned ? "currentColor" : "none"} 
                  stroke="currentColor" 
                  strokeWidth={2} 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            )}
            {onEdit && (
              <button onClick={() => onEdit(report)} title="Edit filters" className="p-1.5 rounded-lg text-gray-500 hover:text-blue-500 hover:bg-blue-50 transition-colors ml-0.5">
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onDuplicate && (
              <button onClick={() => onDuplicate(report)} title="Duplicate card" className="p-1.5 rounded-lg text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors ml-0.5">
                <DocumentDuplicateIcon className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => onDelete(report.id)} title="Delete card" className="p-1.5 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors ml-0.5">
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Loading / Error states */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <ArrowPathIcon className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        )}
        {!loading && error && (
          <div className="flex-1 flex items-center justify-center text-red-400 text-xs text-center px-4">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Metrics */}
            {renderMetrics()}

            {/* View toggle bar */}
            <div className="flex items-center justify-between mb-3 bg-gray-50 p-1 rounded-xl border border-gray-100 shrink-0 select-none">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewMode('graph')}
                  title="Graph View"
                  className={`p-1.5 rounded-lg transition-all ${viewMode === 'graph' ? 'bg-white shadow-sm text-blue-600 border border-blue-100' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <ChartBarSquareIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setViewMode('list'); if (transactions.length === 0) fetchTransactions(0); }}
                  title="List View"
                  className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600 border border-blue-100' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Chart type sub-toggles — only visible in graph mode with breakdown */}
              {viewMode === 'graph' && layout === 'kpi_breakdown' && breakdown.length > 0 && (
                <div className="flex items-center gap-1 pl-2 border-l border-gray-200 ml-2">
                  <button
                    onClick={() => setChartType('bar')}
                    title="Bar Chart"
                    className={`p-1.5 rounded-lg transition-all ${chartType === 'bar' ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <Bars3Icon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setChartType('pie')}
                    title="Pie Chart"
                    className={`p-1.5 rounded-lg transition-all ${chartType === 'pie' ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  >
                    <ChartPieIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            {viewMode === 'graph' ? renderChart() : renderList()}
          </>
        )}
      </div>

      {/* Resize Handle */}
      <div
        className="absolute bottom-0 right-0 w-5 h-5 cursor-se-resize flex items-end justify-end p-0.5 group/resize select-none z-10"
        onMouseDown={handleResizeStart}
        title="Drag to resize card"
      >
        <svg className="w-3.5 h-3.5 text-gray-300 group-hover/resize:text-blue-500 transition-colors pointer-events-none" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM14 22H12V20H14V22ZM18 18H16V16H18V18Z"/>
        </svg>
      </div>
    </div>
  );
};

export default AdaptiveReportCard;
